import { useEffect, useState } from 'react';

import {
  ApiData,
  ApiErrorReason,
  RecentInsurerOptions,
  RefDataType,
} from 'scenes/common/types';
import {
  makeApiSuccess,
  makeApiError,
  isApiError,
  getApiData,
} from 'scenes/common/helpers/apiData';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import {
  MotorFunnelState,
  CoverLevel,
  ReferenceData,
} from 'scenes/motor/types';
import analytics from 'scenes/motor/analytics';
import { referenceDataApi } from 'scenes/motor/api';

const triggerAnalyticsErrorEvent = (
  state: MotorFunnelState,
  error: ApiErrorReason | undefined,
  apiName: string
): void => {
  analytics().error(
    state,
    `Reference data - ${apiName}: ${error?.message || ''}`,
    error?.type
  );
};

export const useReferenceData = (): ApiData<ReferenceData> => {
  const funnelState = useFunnelState<MotorFunnelState>();

  const [businessTypes, setBusinessTypes] = useState<RefDataType[]>([]);
  const [financialInstitutions, setFinancialInstitutions] = useState<
    RefDataType[]
  >([]);
  const [recentInsurerOptions, setRecentInsurerOptions] = useState<
    RecentInsurerOptions
  >({
    dontKnowOption: { value: '', label: '' },
    otherOption: { value: '', label: '' },
    notInsuredOption: { value: '', label: '' },
    mostCommonInsurerList: [],
    otherInsurerList: [],
  });
  const [coverLevelList, setCoverLevelList] = useState<CoverLevel[]>([]);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let componentMounted = true;
    const referenceDataApiInstance = referenceDataApi();
    referenceDataApiInstance
      .getInsurers()
      .then((insurerData) => {
        if (componentMounted) {
          if (isApiError(insurerData)) {
            triggerAnalyticsErrorEvent(
              funnelState,
              insurerData.errorReason,
              'Insurers'
            );
            setHasError(true);
          } else {
            setRecentInsurerOptions(getApiData(insurerData));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getCoverLevels()
      .then((coverLevels) => {
        if (componentMounted) {
          if (isApiError(coverLevels)) {
            triggerAnalyticsErrorEvent(
              funnelState,
              coverLevels.errorReason,
              'Insurer cover levels'
            );
            setHasError(true);
          } else {
            setCoverLevelList(getApiData(coverLevels));
          }
        }
      })
      .catch(() => setHasError(true));
    referenceDataApiInstance
      .getBusinessTypes()
      .then((businessTypeOptions) => {
        if (componentMounted) {
          if (isApiError(businessTypeOptions)) {
            triggerAnalyticsErrorEvent(
              funnelState,
              businessTypeOptions.errorReason,
              'Business types'
            );
            setHasError(true);
          } else {
            setBusinessTypes(getApiData(businessTypeOptions));
          }
        }
      })
      .catch(() => setHasError(true));
    referenceDataApiInstance
      .getFinancialInstitutions()
      .then((financialInstitutionOptions) => {
        if (componentMounted) {
          if (isApiError(financialInstitutionOptions)) {
            triggerAnalyticsErrorEvent(
              funnelState,
              financialInstitutionOptions.errorReason,
              'Financial institutions'
            );
            setHasError(true);
          } else {
            setFinancialInstitutions(getApiData(financialInstitutionOptions));
          }
        }
      })
      .catch(() => setHasError(true));

    return () => {
      componentMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return hasError
    ? makeApiError(`Can't get reference data`)
    : makeApiSuccess<ReferenceData>({
        businessTypes,
        financialInstitutions,
        recentInsurerOptions,
        coverLevelList,
      });
};
